import { Quiz, QuizResult } from './quizzes';

export interface CalculatedResult {
  isHybrid: boolean;
  primaryResult: QuizResult;
  secondaryResult?: QuizResult;
  title: string;
  description: string;
  shortName: string;
  persona: string;
}

export function calculateQuizResult(
  quiz: Quiz,
  selectedAnswers: { questionId: number; selectedOptionText: string }[]
): CalculatedResult {
  const scores: Record<string, number> = {};

  // Initialize scores for all results to 0
  Object.keys(quiz.results).forEach((id) => {
    scores[id] = 0;
  });

  // Calculate scores
  selectedAnswers.forEach((answer) => {
    const question = quiz.questions.find((q) => q.id === answer.questionId);
    if (!question) return;

    const option = question.options.find((opt) => opt.text === answer.selectedOptionText);
    if (!option || !option.points) return;

    Object.entries(option.points).forEach(([resultId, points]) => {
      if (scores[resultId] !== undefined) {
        scores[resultId] += points;
      }
    });
  });

  // Find highest score(s)
  let maxScore = -1;
  let topResultIds: string[] = [];

  Object.entries(scores).forEach(([id, score]) => {
    if (score > maxScore) {
      maxScore = score;
      topResultIds = [id];
    } else if (score === maxScore) {
      topResultIds.push(id);
    }
  });

  // Sort topResultIds to ensure deterministic behavior
  topResultIds.sort();

  if (topResultIds.length >= 2) {
    const idA = topResultIds[0];
    const idB = topResultIds[1];
    const resA = quiz.results[idA];
    const resB = quiz.results[idB];

    const shortName = `${resA.shortName} com ${resB.shortName}`;
    const persona = `${resA.persona} ${resB.persona}`;
    const title = `Você é uma mistura de ${resA.shortName} com ${resB.shortName}!`;
    const description = `Você tem o equilíbrio perfeito entre duas personalidades marcantes! De um lado, o espírito de um **${resA.shortName}** (${resA.persona}). Do outro, o estilo de um **${resB.shortName}** (${resB.persona}).\n\n---\n\n### 🚗 ${resA.title}\n\n${resA.description}\n\n---\n\n### 🚗 ${resB.title}\n\n${resB.description}`;

    return {
      isHybrid: true,
      primaryResult: resA,
      secondaryResult: resB,
      title,
      description,
      shortName,
      persona,
    };
  } else {
    const id = topResultIds[0] || Object.keys(quiz.results)[0];
    const res = quiz.results[id];
    
    // In case no results are found at all, return empty placeholders
    if (!res) {
      return {
        isHybrid: false,
        primaryResult: { id: '', title: '', description: '', shortName: '', persona: '' },
        title: '',
        description: '',
        shortName: '',
        persona: '',
      };
    }

    return {
      isHybrid: false,
      primaryResult: res,
      title: res.title,
      description: res.description,
      shortName: res.shortName,
      persona: res.persona,
    };
  }
}

export function resolveSharedResult(quiz: Quiz, resultStr: string): CalculatedResult | null {
  const cleanResult = resultStr.trim();

  // Check if it's a hybrid result, e.g. "Uno com Palio" or "Uno-Palio" or "uno-palio"
  const hybridParts = cleanResult.split(/ com |-|com|_/i).map(s => s.trim().toLowerCase());
  
  if (hybridParts.length >= 2) {
    const partA = hybridParts[0];
    const partB = hybridParts[1];

    // Find the corresponding results in the quiz
    const resA = Object.values(quiz.results).find(
      r => r.id.toLowerCase() === partA || r.shortName.toLowerCase() === partA
    );
    const resB = Object.values(quiz.results).find(
      r => r.id.toLowerCase() === partB || r.shortName.toLowerCase() === partB
    );

    if (resA && resB) {
      const shortName = `${resA.shortName} com ${resB.shortName}`;
      const persona = `${resA.persona} ${resB.persona}`;
      const title = `Você é uma mistura de ${resA.shortName} com ${resB.shortName}!`;
      const description = `Você tem o equilíbrio perfeito entre duas personalidades marcantes! De um lado, o espírito de um **${resA.shortName}** (${resA.persona}). Do outro, o estilo de um **${resB.shortName}** (${resB.persona}).\n\n---\n\n### 🚗 ${resA.title}\n\n${resA.description}\n\n---\n\n### 🚗 ${resB.title}\n\n${resB.description}`;

      return {
        isHybrid: true,
        primaryResult: resA,
        secondaryResult: resB,
        title,
        description,
        shortName,
        persona,
      };
    }
  }

  // Single result
  const res = Object.values(quiz.results).find(
    r => r.id.toLowerCase() === cleanResult.toLowerCase() || r.shortName.toLowerCase() === cleanResult.toLowerCase()
  );

  if (res) {
    return {
      isHybrid: false,
      primaryResult: res,
      title: res.title,
      description: res.description,
      shortName: res.shortName,
      persona: res.persona,
    };
  }

  return null;
}
